import React, { useEffect, useState } from "react";
import Dashboard from "../../layouts/Dashboard";
import { ReactLoading } from "../Dashboard/TinManagement/import";
import {
  CopyOutlined,
  DeleteFilled,
  DeleteOutlined,
  DownOutlined,
  EditFilled,
  LockOutlined,
  MailOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Card, Input, message, Popconfirm, Popover, Table, Result, Tooltip } from "antd";
import { useHistory } from "react-router-dom";
import RuleConfDetails from "./components/RuleConfDetails";
import useAxios from "../../hooks/useAxios";
import moment from "moment";
import { checkBulkPermissions } from "../../utils/checkPermission";
import useRole from "../../hooks/useRole";

const RuleEngineDashboard = () => {
  const history = useHistory();
  const api = useAxios();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [rules, setRules] = useState([]);

  const role = useRole();
  const [permissions, setPermissions] = useState({
    canViewRule: false,
    canCreateRule: false,
    canEditRule: false,
    canCopyRule: false,
    canDeleteRule: false,
    canTransferLeads: false,
  });
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!role) return;
      try {
        const checks = [
          { entityType: "rule_engine_module", permission: "view_rule" },
          { entityType: "rule_engine_module", permission: "create_rule" },
          { entityType: "rule_engine_module", permission: "edit_rule" },
          { entityType: "rule_engine_module", permission: "copy_rule" },
          { entityType: "rule_engine_module", permission: "delete_rule" },
          { entityType: "rule_engine_module", permission: "transfer_leads" }
        ];
        const results = await checkBulkPermissions(role, checks);
        
        const perms = {
          canViewRule: false,
          canCreateRule: false,
          canEditRule: false,
          canCopyRule: false,
          canDeleteRule: false,
          canTransferLeads: false,
        };
        results.forEach(res => {
          if (res.permission === "view_rule") perms.canViewRule = res.allowed;
          if (res.permission === "create_rule") perms.canCreateRule = res.allowed;
          if (res.permission === "edit_rule") perms.canEditRule = res.allowed;
          if (res.permission === "copy_rule") perms.canCopyRule = res.allowed;
          if (res.permission === "delete_rule") perms.canDeleteRule = res.allowed;
          if (res.permission === "transfer_leads") perms.canTransferLeads = res.allowed;
        });
        
        setPermissions(perms);
      } catch (err) {
        console.error(err);
      } finally {
        setCheckingPermissions(false);
      }
    };
    
    fetchPermissions();
  }, [role]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value) => {
        return <span className="capitalize">{value}</span>;
      },
    },
    {
      title: "Assigned Recruiter",
      dataIndex: "recruiter",
      key: "recruiter",
      render: (value) => {
        if (!Array.isArray(value) || value.length === 0) {
          return "-";
        }

        const names = value.map((item) => item?.name).filter(Boolean);
        const first = names[0];
        const restCount = names.length - 1;
        return (
          <Popover
            content={
              <div className="flex flex-col gap-[10px]">
                {names.map((name, i) => (
                  <div key={i} className="capitalize flex gap-[10px]">
                    <Avatar size="small">{name?.charAt(0)}</Avatar>
                    {name}
                  </div>
                ))}
              </div>
            }
            title="All Recruiters"
            trigger="click"
            placement="bottomRight"
          >
            <span className="capitalize cursor-pointer flex gap-[10px] items-center">
              {first ? (
                <>
                  <Avatar size="small">{first?.charAt(0)}</Avatar>
                  {first || "-"}
                </>
              ) : (
                "-"
              )}
              {restCount > 0 && (
                <span
                  className="ml-1 flex items-center justify-center"
                  style={{
                    width: "23px",
                    height: "23px",
                    border: "1px solid #FFA94D",
                    color: "#FA8C16",
                    borderRadius: "50%",
                    fontSize: "12px",
                    fontWeight: 500,
                    backgroundColor: "#FFF7E6",
                  }}
                >
                  +{restCount}
                </span>
              )}
            </span>
          </Popover>
        );
      },
    },
    {
      title: "Updated By",
      dataIndex: "created_by",
      key: "created_by",
      render: (value) => {
        return <span className="capitalize">{value || "-"}</span>;
      },
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
    },
    {
      title: "Last Update",
      dataIndex: "last_update",
      key: "last_update",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => {
        return value ? (
          <span className="capitalize font-semibold">Active</span>
        ) : (
          <span className="capitalize font-semibold text-red-500">
            Inactive
          </span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      render: (value) => {
        return (
          <div className="flex gap-3 items-center">
            {permissions.canTransferLeads && (
              <Tooltip title="Transfer Leads">
                <span
                  onClick={() => history.push(`/rule-engine/transfer/${value}`)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <SyncOutlined style={{ fontSize: "1.2rem", color: "#FA8C16" }} />
                </span>
              </Tooltip>
            )}
            {permissions.canEditRule && (
              <Tooltip title="Edit Rule">
                <span
                  onClick={() => history.push(`/rule-engine/edit/${value}`)}
                  className="cursor-pointer"
                >
                  <EditFilled style={{ fontSize: "1.2rem", color: "grey" }} />
                </span>
              </Tooltip>
            )}
            {permissions.canCopyRule && (
              <Tooltip title="Clone Rule">
                <span
                  onClick={() => history.push(`/rule-engine/clone/${value}`)}
                  className="cursor-pointer"
                >
                  <CopyOutlined style={{ fontSize: "1.2rem", color: "grey" }} />
                </span>
              </Tooltip>
            )}
            {permissions.canDeleteRule && (
              <span className="cursor-pointer">
                <Popconfirm
                  title="Are you sure you want to delete the rule?"
                  description="Are you sure you want to delete the rule?"
                  onConfirm={() => deleteRule(value)}
                  okText="Yes"
                  cancelText="No"
                  placement="left"
                  okButtonProps={{
                    className: "!bg-red-500 hover:!bg-red-600 !border-none",
                  }}
                >
                  <Tooltip title="Delete Rule">
                    <DeleteOutlined className="!text-red-500 !text-[1.2rem]" />
                  </Tooltip>
                </Popconfirm>
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const getRuleEngineData = async (signal = undefined) => {
    try {
      const { data } = await api.get(`/ruleEngine`, {
        tokenType: "user",
        signal,
      });
      const row = [];

      for (let item of data) {
        const groupedConditions = item?.groups?.reduce((acc, group) => {
          const conditions = group?.conditions || [];

          conditions.forEach((ele) => {
            const key = ele?.label;
            const values = Array.isArray(ele?.value) ? ele.value : [ele?.value];

            if (!key) return;

            acc[key] = [...new Set(acc[key] ?  [...acc[key], ...values] : [...values])];
          });

          return acc;
        }, {});



        row.push({
          key: item?._id,
          action: item?._id,
          name: item?.ruleName,
          recruiter: item?.recruiter,
          recruiterSearch: item?.recruiter?.map(ele => ele?.name)?.join(","),
          last_update: moment(item?.updatedAt).format("YYYY-MM-DD hh:mm A"),
          created_date: moment(item?.createdAt).format("YYYY-MM-DD hh:mm A"),
          status: item?.isActive,
          confDetails: groupedConditions,
          created_by: item?.createdBy?.name || "",
        });
      }
      setRules(row);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteRule = async (value) => {
    try {
      setLoading(true);
      await api.delete(`/ruleEngine/${value}`, { tokenType: "user" });
      await getRuleEngineData();
      message.success("Rule Deleted Successfully.")
    } catch (err) {
      console.error(err);
      const error = err?.response?.data?.error;
      message.error(error ? error : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const init = async () => {
      try {
        setLoading(true);
        await getRuleEngineData(controller.signal);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
    return () => {
      controller.abort();
    };
  }, []);

  const filteredRows = rules?.filter((row) => {
    const _search = search.toLowerCase();

    return (
      row?.name?.toLowerCase().includes(_search) ||
      row?.recruiterSearch?.toLowerCase().includes(_search)
    );
  });

  return (
    <Dashboard title="Rule Engine">
      {checkingPermissions ? (
        <div className="flex justify-center items-center h-64">
          <ReactLoading type="spin" color="#0000FF" />
        </div>
      ) : !permissions.canViewRule ? (
        <Card className="mt-3">
          <Result
            icon={<StopOutlined style={{ color: '#ff4d4f' }} />}
            title="You don't have permission to access this page"
            subTitle="Please contact your administrator if you believe this is an error."
          />
        </Card>
      ) : (
        <>
          {loading && (
            <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
          )}
      <div className="py-1 px-1">
        <h4 className="text-[1.5rem] mb-0">Rules - Manage Rules</h4>
        <p className="text-[0.89rem]">
          View and manage user rules in one place.
        </p>
      </div>
      <div>
        <Card>
          <div className="flex flex-col gap-[1.5rem]">
            <div className="flex justify-between">
              <div className="flex-grow">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    borderRadius: "8px",
                    width: "100%",
                    maxWidth: "300px",
                  }}
                  size="large"
                  placeholder="Search by name and recruiter..."
                  prefix={<SearchOutlined />}
                />
              </div>
              {permissions.canCreateRule && (
                <button
                  onClick={() => history.push(`/rule-engine/create`)}
                  data-testid="add-new"
                  className="btn btn-primary flex gap-[10px] items-center"
                >
                  <PlusOutlined style={{ fontSize: "1.1rem", fontWeight: 600 }} />
                  <span className="flex items-center text-[16px] font-semibold">
                    Create New
                  </span>
                </button>
              )}
            </div>
            <div>
              <Table
                columns={columns}
                dataSource={filteredRows}
                pagination={{
                  total: filteredRows?.length,
                  pageSizeOptions: ["5", "10", "30"],
                  showSizeChanger: true,
                }}
                expandable={{
                  expandedRowKeys,
                  expandedRowRender: (record) => {
                    return <RuleConfDetails details={record?.confDetails} />;
                  },
                  onExpand: (expanded, record) => {
                    setExpandedRowKeys(expanded ? [record.key] : []);
                  },
                  expandIcon: ({ expanded, onExpand, record }) => (
                    <span
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      {expanded ? <DownOutlined /> : <RightOutlined />}
                    </span>
                  ),
                }}
                showSorterTooltip={false}
                scroll={{ x: true }}
                style={{ whiteSpace: "pre" }}
                exportFileName="details"
                size="small"
              />
            </div>
          </div>
        </Card>
      </div>
        </>
      )}
    </Dashboard>
  );
};

export default RuleEngineDashboard;
